import React, { useState, useRef, useEffect } from 'react';
import './Recognition.css';
import * as faceapi from 'face-api.js';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import AddFaceForm from './AddFaceForm';
import { sendNewFaceToBackend, getKnownFacesDataFromDB } from '../services/data';
import { create, all } from 'mathjs';

const Recognition = () => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [knownFaces, setKnownFaces] = useState([]);
  const [detectionStarted, setDetectionStarted] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [showAddFaceForm, setShowAddFaceForm] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const navigate = useNavigate();
  const math = create(all);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL;
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL + '/models/face_landmark_68');
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL + '/models/face_recognition');
      await faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL + '/models/age_gender_model');
      await faceapi.nets.mtcnn.loadFromUri(MODEL_URL + '/models/mtcnn');
    };

    loadModels();
  }, []);

  const handleToggleAddFaceForm = () => {
    setShowAddFaceForm(!showAddFaceForm);
  };
  const handleCloseAddFaceForm = () => {
    setShowAddFaceForm(false);
  }

  useEffect(() => {
    const loadUserData = async () => {
      const userDetailsCookie = Cookies.get('facereco_user_details');
      if (userDetailsCookie) {
        const parsedUserDetails = JSON.parse(userDetailsCookie);
        setUserDetails(parsedUserDetails);
        const data = {
          'email': parsedUserDetails.email,
        }
        const response = await getKnownFacesDataFromDB(data);
        setKnownFaces(Object.values(response.faces));
      } else {
        navigate('/');
      }
    };
    loadUserData();
  }, [showAddFaceForm, navigate]);

  useEffect(() => {
    const getCameraList = async () => {
      await navigator.mediaDevices.getUserMedia({ video: true });
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        setCameras(videoDevices);
        setSelectedCamera(videoDevices[0]); // Set the default camera as the first device in the list
      } catch (error) {
        console.error('Error accessing video devices:', error);
      }
    };

    getCameraList();
  }, []);

  const calculateL2NormDistance = React.useCallback((descriptor1, descriptor2) => {
    const squaredDifferences = math.subtract(descriptor1, descriptor2).map((value) => value ** 2);
    const sumOfSquaredDifferences = math.sum(squaredDifferences);
    const distance = math.sqrt(sumOfSquaredDifferences);
    return distance;
  }, [math]);

  useEffect(() => {
    const video = videoRef.current; // Capture ref value at effect start

    const startVideoStream = async () => {
      try {
        if (!selectedCamera) return;

        const constraints = {
          video: {
            deviceId: { exact: selectedCamera.deviceId },
          },
        };
        video.srcObject = await navigator.mediaDevices.getUserMedia(constraints);
        video.addEventListener('loadedmetadata', async () => {
          const displaySize = {
            width: video.videoWidth,
            height: video.videoHeight,
          };
          faceapi.matchDimensions(canvasRef.current, displaySize);
          const context = canvasRef.current.getContext('2d', { willReadFrequently: true });
          context.clearRect(0, 0, displaySize.width, displaySize.height);

          const detectionInterval = setInterval(async () => {
            if (detectionStarted) {
              const detections = await faceapi
                .detectAllFaces(video, new faceapi.MtcnnOptions({ scoreThreshold: 0.5 }))
                .withFaceLandmarks()
                .withAgeAndGender()
                .withFaceDescriptors();

              const resizedDetections = faceapi.resizeResults(detections, displaySize);
              context.clearRect(0, 0, displaySize.width, displaySize.height);
              faceapi.draw.drawDetections(canvasRef.current, resizedDetections);

              resizedDetections.forEach(async (detection) => {
                const { x, y, width, height } = detection.detection.box;
                const faceDescriptor = detection.descriptor;
                let minDistance = Infinity;
                let minDistanceIndex = -1;

                knownFaces.forEach((knownFace, index) => {
                  const knownDescriptors = knownFace.descriptors;
                  knownDescriptors.forEach((knownDescriptor) => {
                    const knownDescriptorArray = new Float32Array(Object.values(knownDescriptor));
                    // const distance = faceapi.euclideanDistance(faceDescriptor, knownDescriptorArray);
                    const distance = calculateL2NormDistance(Array.from(faceDescriptor), Array.from(knownDescriptorArray));

                    if (distance < minDistance) {
                      minDistance = distance;
                      minDistanceIndex = index;
                    }
                  });
                });

                if (minDistanceIndex >= 0) {
                  const { name, age, gender } = knownFaces[minDistanceIndex];

                  // Set a dynamic threshold based on the distance of the closest match
                  // const threshold = minDistance * 1.1;

                  if (minDistance < 0.6) {
                    const recognitionResult = `${name} ${gender === 'male' ? 'M' : 'F'} ${age}`;
                    const faceRectangle = new faceapi.draw.DrawBox(
                      { x, y, width, height },
                      { label: recognitionResult }
                    );
                    faceRectangle.draw(canvasRef.current);
                  } else {
                    const unknownResult = `Unknown ${detection.gender === 'male' ? 'M' : 'F'} ${faceapi.utils.round(detection.age, 0)}`;
                    const faceRectangle = new faceapi.draw.DrawBox(
                      { x, y, width, height },
                      { label: unknownResult }
                    );
                    faceRectangle.draw(canvasRef.current);
                  }
                }
              });
            }
          }, 200);

          // Cleanup function to clear interval when component unmounts or dependencies change
          return () => {
            clearInterval(detectionInterval);
          };
        });
      } catch (error) {
        console.error('Error accessing webcam:', error);
      }
    };

    startVideoStream();

    // Cleanup function for the effect
    return () => {
      if (video && video.srcObject) {
        const tracks = video.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [knownFaces, selectedCamera, detectionStarted, calculateL2NormDistance]);

  const handleCameraSwap = async (deviceId) => {
    try {
      const selectedCamera = cameras.find((camera) => camera.deviceId === deviceId);

      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());

      setSelectedCamera(selectedCamera);
    } catch (error) {
      console.error('Error swapping camera:', error);
    }
  };

  const handleStartDetection = () => {
    setDetectionStarted(true);
  };

  const generateFaceEncoding = async (image) => {
    let img;
    if (typeof image === 'string') {
      img = await faceapi.fetchImage(image);
    } else if (image instanceof Blob) {
      const imageUrl = URL.createObjectURL(image);
      img = await faceapi.fetchImage(imageUrl);
    } else {
      throw new Error('Invalid image format');
    }
    const detection = await faceapi.detectSingleFace(img, new faceapi.MtcnnOptions({ scoreThreshold: 0.5 })).withFaceLandmarks().withFaceDescriptor();
    if (!detection) {
      window.alert('No face detected in the image, please upload an image having only one face');
      return null;
    }

    // Generate the face descriptor (encoding)
    // const faceDescriptor = await faceapi.computeFaceDescriptor(img,detection.landmarks);
    const faceDescriptor = detection.descriptor;
    // Return the face encoding as an array
    return faceDescriptor;
  }

  const handleSubmitAddFace = async (formData) => {
    const { name, email, age, gender, image1, image2 } = formData;

    // Perform face encodings calculations for image1
    const encoding1 = await generateFaceEncoding(image1);

    // Perform face encodings calculations for image2
    const encoding2 = await generateFaceEncoding(image2);

    if (!encoding1 || !encoding2) {
      setShowAddFaceForm(true);
      return;
    }
    // Create a JSON object with the data
    const data = {
      account: userDetails.email,
      name,
      email,
      age,
      gender,
      descriptors: [encoding1, encoding2],
    };

    // Log the data
    console.log(data);
    const response = await sendNewFaceToBackend(data);
    window.alert(response.message);
    if (response.message === "Face added successfully") {
      const cacheName = "known-faces-data";
      const cache = await caches.open(cacheName);
      // const url = process.env.REACT_APP_TESTING_BACKEND_URL + "/get_known_faces";
      const url = process.env.REACT_APP_PUBLIC_BACKEND_URL + "/get_known_faces";
      const cacheResponse = await cache.match(url);
      if (cacheResponse) {
        await cache.delete(url);
      }
    }
  };
  const handleHomeButtonClick = () => {
    navigate('/');
    window.location.reload();
  };
  const handleDashboardClick = () => {
    navigate('/dashboard');
    window.location.reload();
  };

  return (
    <div className="row">
      {/* Left Sidebar */}
      <div className="reco-navbar">
        <button className="reco-hamburger" onClick={() => setSidebarVisible(!sidebarVisible)}>
          &#9776;
        </button>
      </div>
      <div className={`column left left-sidebar ${sidebarVisible ? 'show' : ''}`}>
        <div className="sidebar-header">
          <button className="home-button" onClick={handleHomeButtonClick}>
            <span className="home-text">Home</span>
          </button>
          <button className="dashboard-button" onClick={handleDashboardClick}>Dashboard</button>
        </div>
        <div className="sidebar-content">
          {/* scrollable content */}
          {knownFaces.map((face) => (
            <div key={face.email} className="known-face-card">
              <div className="known-face-name">{face.name}</div>
            </div>
          ))}
        </div>
        <div className="sidebar-footer">
          <img src={userDetails.picture} alt="Profile" className="profile-picture" />
          <div className="name">{userDetails.name}</div>
          <div className="menu-button">
            <button className="menu-button">. . .</button>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className={`column right ${sidebarVisible ? 'hide' : ''}`}>
        <div className="video-pan">
          <video ref={videoRef} autoPlay playsInline muted className="video-element"></video>
          <canvas ref={canvasRef} className="canvas-element"></canvas>
        </div>
        <div className="camera-selection">
          <label htmlFor="camera">Select Camera : </label>
          <select
            id="camera"
            value={selectedCamera?.deviceId}
            onChange={(event) => handleCameraSwap(event.target.value)}
            disabled={detectionStarted}
          >
            {cameras.map((camera) => (
              <option key={camera.deviceId} value={camera.deviceId}>
                {camera.label}
              </option>
            ))}
          </select>
        </div>
        <div className="button-pan">
          <div className="button-container">
            <button
              className="start-detection-btn"
              onClick={handleStartDetection}
              disabled={detectionStarted}
            >
              Start Detection
            </button>
            <button className="add-face-btn" onClick={handleToggleAddFaceForm}>
              Add Face
            </button>
          </div>
          <div className="button-text">
            <p>© 2023 FaceReco. All rights reserved.</p>
          </div>
        </div>


      </div>

      {showAddFaceForm && <AddFaceForm onClose={handleCloseAddFaceForm} onSubmit={handleSubmitAddFace} />}
    </div>
  );
};

export default Recognition;
